import React, { useState, useEffect, useRef } from 'react';
import { Save, Users, Calendar, Trophy, Database, Shield, TestTube, Grid, Settings as SettingsIcon, Plus, Edit, Trash2, UserPlus, Lock, Eye, EyeOff, RefreshCw, Mail, CheckCircle, Clock } from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useSettings } from '../../contexts/SettingsContext';
import { getApiUrl } from "../../config/api";
import axios from 'axios';

export default function Settings() {
  const { showNotification } = useNotification();
  const { subscription, getMaxFamilyMembers, canAddFamilyMember, getTierName } = useSubscription();
  const { widgetSettings, updateWidgetSettings, saveSettings: saveWidgetSettings } = useSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Don't default to sync tab for horsemgmt tier users
    const tierName = getTierName();
    return tierName === 'horsemgmt' ? 'profile' : 'sync';
  });
  
  // Data Sync state
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [lastResultsSyncDate, setLastResultsSyncDate] = useState(null);
  const [lastMembershipsSyncDate, setLastMembershipsSyncDate] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingResults, setIsSyncingResults] = useState(false);
  const [isSyncingMemberships, setIsSyncingMemberships] = useState(false);
  const [confirmedEmails, setConfirmedEmails] = useState([]);
  const [syncEmail, setSyncEmail] = useState('');
  const [syncEmailLoading, setSyncEmailLoading] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Unified Association Settings
  const [associations, setAssociations] = useState([]);
  const [availableFamilyMembers, setAvailableFamilyMembers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [newAssociation, setNewAssociation] = useState({
    familyMemberId: '',
    associationName: '',
    associationKey: '',
    userId: '',
    userPassword: ''
  });

  const isFamilyPlan = getMaxFamilyMembers() > 1;
  const canAddMoreAssociations = true; // Always allow adding more associations
  const isHorsemgmtTier = getTierName() === 'horsemgmt';

  const [availableAssociations, setAvailableAssociations] = useState([]);
  const [hasApprovedEmail, setHasApprovedEmail] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const loadAssociationsList = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/associations/list'));
      setAvailableAssociations(response.data || []);
    } catch (error) {
      console.error('Error loading associations list:', error);
    }
  };

  const checkApprovalStatus = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/data-import/check-approval'));
      setHasApprovedEmail(response.data.hasApprovedEmail);
    } catch (error) {
      console.error('Error checking approval status:', error);
    }
  };

  const handleImportNewData = async () => {
    if (!hasApprovedEmail) {
      showNotification('Please complete email confirmation first', 'error');
      return;
    }

    setIsImporting(true);
    try {
      const response = await axios.post(getApiUrl('/api/data-import/retry'));
      if (response.data.success) {
        showNotification('Data imported successfully!', 'success');
        // Reload associations to show new data
        loadSettings();
      } else {
        showNotification('Import failed', 'error');
      }
    } catch (error) {
      console.error('Error importing data:', error);
      showNotification('Failed to import data', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadAssociationsList();
    checkApprovalStatus();
  }, []);

  useEffect(() => {
    checkApprovalStatus();
  }, [associations]);

  // Switch away from sync tab if user is on horsemgmt tier
  useEffect(() => {
    if (isHorsemgmtTier && activeTab === 'sync') {
      setActiveTab('profile');
    }
  }, [isHorsemgmtTier, activeTab]);


  
  // Load family members after associations are loaded
  useEffect(() => {
    if (isFamilyPlan && associations.length > 0) {
      loadFamilyMembers();
    }
  }, [isFamilyPlan, associations]);
  
  // Load family members on initial load if no associations exist
  useEffect(() => {
    if (isFamilyPlan && associations.length === 0) {
      loadFamilyMembers();
    }
  }, [isFamilyPlan]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      // Load settings to get last sync dates
      const settingsResponse = await axios.get(getApiUrl('/api/settings'));
      if (settingsResponse.data) {
        if (settingsResponse.data.lastEntriesSyncDate) {
          setLastSyncDate(settingsResponse.data.lastEntriesSyncDate);
        }
        if (settingsResponse.data.lastResultsSyncDate) {
          setLastResultsSyncDate(settingsResponse.data.lastResultsSyncDate);
        }
        if (settingsResponse.data.lastMembershipsSyncDate) {
          setLastMembershipsSyncDate(settingsResponse.data.lastMembershipsSyncDate);
        }
      }
      
      // Load confirmed emails
      const emailsResponse = await axios.get(getApiUrl('/api/entries/confirmed-emails'));
      if (emailsResponse.data) {
        setConfirmedEmails(emailsResponse.data.emails || []);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showNotification('Failed to load settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSyncEntries = async () => {
    if (confirmedEmails.length === 0) {
      showNotification('Please confirm at least one email address first', 'error');
      return;
    }
    
    setIsSyncing(true);
    try {
      const response = await axios.get(getApiUrl('/api/entries/delta-sync'));
      
      if (response.data && response.data.success) {
        showNotification(
          `Entries sync completed! Added ${response.data.summary.entriesAdded} entries, updated ${response.data.summary.entriesUpdated} entries.`,
          'success'
        );
        // Reload settings to get updated sync date
        await loadSettings();
      } else {
        showNotification('Entries sync completed with some errors', 'warning');
      }
    } catch (error) {
      console.error('Error syncing entries:', error);
      showNotification(
        error.response?.data?.error || 'Failed to sync entries',
        'error'
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncResults = async () => {
    if (confirmedEmails.length === 0) {
      showNotification('Please confirm at least one email address first', 'error');
      return;
    }
    
    setIsSyncingResults(true);
    try {
      const response = await axios.get(getApiUrl('/api/results/delta-sync'));
      
      if (response.data && response.data.success) {
        showNotification(
          `Results sync completed! Added ${response.data.summary.showsAdded} shows, updated ${response.data.summary.showsUpdated} shows.`,
          'success'
        );
        // Reload settings to get updated sync date
        await loadSettings();
      } else {
        showNotification('Results sync completed with some errors', 'warning');
      }
    } catch (error) {
      console.error('Error syncing results:', error);
      showNotification(
        error.response?.data?.error || 'Failed to sync results',
        'error'
      );
    } finally {
      setIsSyncingResults(false);
    }
  };

  const handleSyncMemberships = async () => {
    if (confirmedEmails.length === 0) {
      showNotification('Please confirm at least one email address first', 'error');
      return;
    }
    
    setIsSyncingMemberships(true);
    try {
      const response = await axios.get(getApiUrl('/api/associations/memberships/delta-sync'));
      
      if (response.data && response.data.success) {
        showNotification(
          `Memberships sync completed! Added ${response.data.summary.membershipsAdded} memberships, skipped ${response.data.summary.membershipsSkipped} duplicates.`,
          'success'
        );
        // Reload settings to get updated sync date
        await loadSettings();
      } else {
        showNotification('Memberships sync completed with some errors', 'warning');
      }
    } catch (error) {
      console.error('Error syncing memberships:', error);
      showNotification(
        error.response?.data?.error || 'Failed to sync memberships',
        'error'
      );
    } finally {
      setIsSyncingMemberships(false);
    }
  };

  const handleSyncAll = async () => {
    if (confirmedEmails.length === 0) {
      showNotification('Please confirm at least one email address first', 'error');
      return;
    }
    
    setIsSyncing(true);
    setIsSyncingResults(true);
    setIsSyncingMemberships(true);
    
    try {
      // Sync entries, results, and memberships in parallel
      const [entriesResponse, resultsResponse, membershipsResponse] = await Promise.allSettled([
        axios.get(getApiUrl('/api/entries/delta-sync')),
        axios.get(getApiUrl('/api/results/delta-sync')),
        axios.get(getApiUrl('/api/associations/memberships/delta-sync'))
      ]);

      const entriesSuccess = entriesResponse.status === 'fulfilled' && entriesResponse.value.data?.success;
      const resultsSuccess = resultsResponse.status === 'fulfilled' && resultsResponse.value.data?.success;
      const membershipsSuccess = membershipsResponse.status === 'fulfilled' && membershipsResponse.value.data?.success;

      if (entriesSuccess && resultsSuccess && membershipsSuccess) {
        const entriesSummary = entriesResponse.value.data.summary;
        const resultsSummary = resultsResponse.value.data.summary;
        const membershipsSummary = membershipsResponse.value.data.summary;
        showNotification(
          `Sync completed! Entries: ${entriesSummary.entriesAdded} added, ${entriesSummary.entriesUpdated} updated. Results: ${resultsSummary.showsAdded} added, ${resultsSummary.showsUpdated} updated. Memberships: ${membershipsSummary.membershipsAdded} added, ${membershipsSummary.membershipsSkipped} skipped.`,
          'success'
        );
      } else if (entriesSuccess || resultsSuccess || membershipsSuccess) {
        showNotification('Sync completed with some errors', 'warning');
      } else {
        showNotification('Sync failed', 'error');
      }

      // Reload settings to get updated sync dates
      await loadSettings();
    } catch (error) {
      console.error('Error syncing:', error);
      showNotification('Failed to sync', 'error');
    } finally {
      setIsSyncing(false);
      setIsSyncingResults(false);
      setIsSyncingMemberships(false);
    }
  };
  
  const handleAddSyncEmail = async () => {
    if (!syncEmail || !syncEmail.includes('@')) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }
    
    setSyncEmailLoading(true);
    try {
      const response = await axios.post(
        getApiUrl('/api/entries/confirm-email'),
        { email: syncEmail }
      );
      
      if (response.data.alreadyApproved) {
        showNotification('This email is already confirmed', 'info');
      } else {
        showNotification('Confirmation email sent! Check your inbox.', 'success');
      }
      setSyncEmail('');
      await loadSettings(); // Reload to refresh confirmed emails list
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send confirmation email';
      showNotification(errorMessage, 'error');
      
      // If token is invalid, suggest refreshing
      if (errorMessage.includes('token') || error.response?.status === 401 || error.response?.status === 403) {
        console.error('Authentication error - token may be expired. Please try logging out and back in.');
      }
    } finally {
      setSyncEmailLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/settings/family-members'));
      const allFamilyMembers = response.data || [];
      
      // All family members should always be available for multiple associations
      setAvailableFamilyMembers(allFamilyMembers);
    } catch (error) {
      console.error('Error loading family members', error);
    }
  };



  const saveSettings = async () => {
    try {
      setIsLoading(true);
      
      // Save dashboard settings using context
      const success = await saveWidgetSettings(widgetSettings);
      
      if (success) {
        showNotification('Settings saved successfully', 'success');
      } else {
        showNotification('Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Failed to save settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAssociation = async () => {
    try {
      if (!newAssociation.associationName || !newAssociation.userId || !newAssociation.userPassword) {
        showNotification('Please fill in all required fields', 'error');
        return;
      }

      if (isFamilyPlan && !newAssociation.familyMemberId) {
        showNotification('Please select a family member', 'error');
        return;
      }

      let response;
      if (newAssociation.familyMemberId && newAssociation.familyMemberId !== 'primary') {
        // Add family association - store family_member_id instead of target_user_id
        response = await axios.post(getApiUrl('/api/user-associations'), {
          family_member_id: newAssociation.familyMemberId,
          association_key: newAssociation.associationKey,
          association_name: newAssociation.associationName,
          user_id_at_association: newAssociation.userId,
          user_password_at_association: newAssociation.userPassword
        });
      } else {
        // Add association for current user using the unified user associations API
        response = await axios.post(getApiUrl('/api/user-associations'), {
          association_key: newAssociation.associationKey,
          association_name: newAssociation.associationName,
          user_id_at_association: newAssociation.userId,
          user_password_at_association: newAssociation.userPassword
        });
      }

      if (response.data) {
        showNotification('Association added successfully', 'success');
        setShowAddModal(false);
        setNewAssociation({
          familyMemberId: '',
          associationName: '',
          associationKey: '',
          userId: '',
          userPassword: '',
          displayName: ''
        });
        loadSettings();
        // Refresh available family members after adding
        if (isFamilyPlan) {
          setTimeout(() => {
            loadFamilyMembers();
          }, 100); // Small delay to ensure associations are updated
        }
      }
    } catch (error) {
      console.error('Error adding association:', error);
      showNotification('Failed to add association', 'error');
    }
  };

  const handleUpdateAssociation = async (association) => {
    try {
      // Update association using the new user associations API
      await axios.put(getApiUrl(`/api/user-associations/${association.id}`), {
        association_key: association.associationKey,
        association_name: association.associationName,
        user_id_at_association: association.userId,
        user_password_at_association: association.userPassword
      });

      showNotification('Association updated successfully', 'success');
      setEditingAssociation(null);
      setShowAddModal(false); // Close the modal
      loadSettings();
      // Refresh available family members after updating
      if (isFamilyPlan) {
        setTimeout(() => loadFamilyMembers(), 100); // Small delay to ensure associations are updated
      }
    } catch (error) {
      console.error('Error updating association:', error);
      showNotification('Failed to update association', 'error');
    }
  };

  const handleDeleteAssociation = async (associationId) => {
    try {
      // Delete association using the new user associations API
      await axios.delete(getApiUrl(`/api/user-associations/${associationId}`));

      showNotification('Association deleted successfully', 'success');
      loadSettings();
      // Refresh available family members after deleting
      if (isFamilyPlan) {
        setTimeout(() => loadFamilyMembers(), 100); // Small delay to ensure associations are updated
      }
    } catch (error) {
      console.error('Error deleting association:', error);
      showNotification('Failed to delete association', 'error');
    }
  };

  const handleEditAssociation = (association) => {
    setEditingAssociation(association);
    setNewAssociation({
      familyMemberId: association.familyMemberId || '',
      associationName: association.associationName || '',
      associationKey: association.associationKey || '',
      userId: association.userId || '',
      userPassword: association.userPassword || ''
    });
    setShowAddModal(true);
  };

  const handleCancelEdit = () => {
    setEditingAssociation(null);
    setShowAddModal(false);
    setNewAssociation({
      familyMemberId: '',
      associationName: '',
      userId: '',
      userPassword: ''
    });
  };

  const handleInputChange = (field, value) => {
    if (editingAssociation) {
      // Update the editingAssociation state for the form
      setEditingAssociation(prev => ({ ...prev, [field]: value }));
    } else {
      // Update the new association form
      setNewAssociation(prev => ({ ...prev, [field]: value }));
    }
  };

  // Password change functions
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      minLength: password.length >= minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar
    };
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showNotification('Please fill in all password fields', 'error');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotification('New passwords do not match', 'error');
      return;
    }

    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      showNotification('New password does not meet security requirements', 'error');
      return;
    }

    setIsChangingPassword(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/api/auth/change-password'), {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      showNotification('Password changed successfully', 'success');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      if (error.response?.data?.error) {
        showNotification(error.response.data.error, 'error');
      } else {
        showNotification('Failed to change password', 'error');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <SettingsIcon className="w-6 h-6 sm:w-8 sm:h-8" />
            Settings
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6 sm:mb-8">
          <nav className="-mb-px -mx-4 sm:mx-0 flex gap-2 sm:gap-8 overflow-x-auto sm:overflow-visible px-4 sm:px-0 scrollbar-hide">
            {!isHorsemgmtTier && (
              <button
                onClick={() => setActiveTab('sync')}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center flex-shrink-0 ${
                  activeTab === 'sync'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <RefreshCw size={14} className="mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">KCI Rodeo Sync</span>
                <span className="sm:hidden">KCI Sync</span>
              </button>
            )}
            <button
              onClick={() => setActiveTab('password')}
              className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center flex-shrink-0 ${
                activeTab === 'password'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Lock size={14} className="mr-1 sm:mr-2 flex-shrink-0" />
              <span className="hidden sm:inline">Change Password</span>
              <span className="sm:hidden">Password</span>
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center flex-shrink-0 ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Grid size={14} className="mr-1 sm:mr-2 flex-shrink-0" />
              <span className="hidden sm:inline">Dashboard Widgets</span>
              <span className="sm:hidden">Widgets</span>
            </button>
          </nav>
        </div>

        {/* Data Sync Tab */}
        {!isHorsemgmtTier && activeTab === 'sync' && (
          <div className="space-y-6 overflow-x-hidden">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">KCI Rodeo Sync</h2>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                Sync your entries, results, and memberships from KCIRodeo automatically. Confirm your email addresses to enable syncing.
              </p>
              
              {/* Manual Sync Section */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <RefreshCw size={20} />
                  Manual Sync
                </h3>
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Entries:</strong> {lastSyncDate ? new Date(lastSyncDate).toLocaleString() : 'Never'}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Results:</strong> {lastResultsSyncDate ? new Date(lastResultsSyncDate).toLocaleString() : 'Never'}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Memberships:</strong> {lastMembershipsSyncDate ? new Date(lastMembershipsSyncDate).toLocaleString() : 'Never'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Sync will check for new entries, results, and memberships from all confirmed email addresses.
                      </p>
                    </div>
                    <button
                      onClick={handleSyncAll}
                      disabled={(isSyncing || isSyncingResults || isSyncingMemberships) || confirmedEmails.length === 0}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {(isSyncing || isSyncingResults || isSyncingMemberships) ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={16} />
                          Sync Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Confirmed Emails Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail size={20} />
                  Confirmed Email Addresses
                </h3>
                
                {/* Add Email Input */}
                <div className="mb-4 flex gap-2">
                  <input
                    type="email"
                    value={syncEmail}
                    onChange={(e) => setSyncEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddSyncEmail();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddSyncEmail}
                    disabled={syncEmailLoading || !syncEmail}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {syncEmailLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail size={16} />
                        Add Email
                      </>
                    )}
                  </button>
                </div>
                
                {/* Confirmed Emails List */}
                {confirmedEmails.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Mail size={40} className="mx-auto mb-4 text-gray-400" />
                    <h4 className="text-base font-medium text-gray-900 mb-2">No confirmed emails</h4>
                    <p className="text-gray-500 mb-4 text-sm">
                      Add an email address above to start syncing your data from KCIRodeo.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {confirmedEmails.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-gray-900">{item.email}</p>
                            <p className="text-xs text-gray-600">
                              Confirmed {item.confirmedAt ? new Date(item.confirmedAt).toLocaleDateString() : 'recently'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Info Box */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">How it works</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Confirm your email address(es) used with KCIRodeo associations</li>
                  <li>• MyTrailer will automatically check for new entries weekly</li>
                  <li>• You can also manually sync anytime using the "Sync Now" button</li>
                  <li>• You can confirm multiple email addresses if you use different emails for different associations</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Tab */}
        {activeTab === 'password' && (
          <div className="space-y-6 overflow-x-hidden">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Lock size={20} />
                Change Password
              </h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-3 sm:py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                      placeholder="Enter your current password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.current ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-3 sm:py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                      placeholder="Enter your new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.new ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {/* Password strength indicator */}
                  {passwordForm.newPassword && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-600 mb-1">Password requirements:</div>
                      <div className="space-y-1">
                        <div className={`text-xs flex items-center ${passwordForm.newPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-1">{passwordForm.newPassword.length >= 8 ? '✓' : '○'}</span>
                          At least 8 characters
                        </div>
                        <div className={`text-xs flex items-center ${/[A-Z]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-1">{/[A-Z]/.test(passwordForm.newPassword) ? '✓' : '○'}</span>
                          One uppercase letter
                        </div>
                        <div className={`text-xs flex items-center ${/[a-z]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-1">{/[a-z]/.test(passwordForm.newPassword) ? '✓' : '○'}</span>
                          One lowercase letter
                        </div>
                        <div className={`text-xs flex items-center ${/\d/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-1">{/\d/.test(passwordForm.newPassword) ? '✓' : '○'}</span>
                          One number
                        </div>
                        <div className={`text-xs flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-1">{/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword) ? '✓' : '○'}</span>
                          One special character
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-3 sm:py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                      placeholder="Confirm your new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.confirm ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {/* Password match indicator */}
                  {passwordForm.confirmPassword && (
                    <div className="mt-1">
                      <div className={`text-xs flex items-center ${passwordForm.newPassword === passwordForm.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="mr-1">{passwordForm.newPassword === passwordForm.confirmPassword ? '✓' : '○'}</span>
                        {passwordForm.newPassword === passwordForm.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isChangingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 sm:py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base sm:text-sm"
                  >
                    {isChangingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        Change Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Security Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Security Tips</h4>
              <ul className="text-xs text-blue-700 space-y-1 break-words">
                <li>• Use a unique password that you don't use elsewhere</li>
                <li>• Consider using a password manager to generate and store secure passwords</li>
                <li>• Avoid using personal information in your password</li>
                <li>• Change your password regularly for better security</li>
              </ul>
            </div>
          </div>
        )}

        {/* Dashboard Widgets Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6 overflow-x-hidden">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Grid size={20} />
                Dashboard Widget Configuration
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Layout
                  </label>
                  <select
                    value={widgetSettings.layout}
                    onChange={(e) => updateWidgetSettings({ layout: e.target.value })}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                  >
                    <option value="grid">Grid</option>
                    <option value="list">List</option>
                    <option value="compact">Compact</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Show Widget Controls
                  </label>
                  <input
                    type="checkbox"
                    checked={widgetSettings.showWidgetControls}
                    onChange={(e) => updateWidgetSettings({ showWidgetControls: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={saveSettings}
                  disabled={isLoading}
                  className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base sm:text-sm"
                >
                  <Save size={16} />
                  Save Dashboard Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

             {/* Add/Edit Association Modal */}
       {showAddModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] flex items-center justify-center p-4">
           <div className="relative mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              {/* Close button */}
              <button
                onClick={handleCancelEdit}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAssociation ? 'Edit Association' : 'Add Association'}
              </h3>
              
              <form className="space-y-4 sm:space-y-6" onSubmit={(e) => e.preventDefault()}>
                {isFamilyPlan && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Family Member
                    </label>
                    <select
                      value={editingAssociation ? editingAssociation.familyMemberId || 'primary' : newAssociation.familyMemberId}
                      onChange={(e) => handleInputChange('familyMemberId', e.target.value)}
                      className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                      disabled={editingAssociation?.isPrimary}
                    >
                      <option value="">Select family member</option>
                      <option value="primary">{currentUserName || 'You'}</option>
                      {availableFamilyMembers.length > 0 ? (
                        availableFamilyMembers.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name} {member.relationship ? `(${member.relationship})` : ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No available family members</option>
                      )}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Association
                  </label>
                  <select
                    value={editingAssociation ? editingAssociation.associationKey || '' : newAssociation.associationKey}
                    onChange={(e) => {
                      const key = e.target.value;
                      const assoc = availableAssociations.find(a => a.associationKey === key);
                      if (editingAssociation) {
                        setEditingAssociation(prev => ({
                          ...prev,
                          associationKey: key,
                          associationName: assoc?.associationName || ''
                        }));
                      } else {
                        setNewAssociation(prev => ({
                          ...prev,
                          associationKey: key,
                          associationName: assoc?.associationName || ''
                        }));
                      }
                    }}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                  >
                    <option value="">Select association</option>
                    {availableAssociations.map(a => (
                      <option key={a.associationKey} value={a.associationKey}>{a.associationName}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User ID
                  </label>
                  <input
                    type="text"
                    value={editingAssociation ? editingAssociation.userId || '' : newAssociation.userId}
                    onChange={(e) => handleInputChange('userId', e.target.value)}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                    placeholder="Enter user ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Password
                  </label>
                  <input
                    type="password"
                    value={editingAssociation ? editingAssociation.userPassword || '' : newAssociation.userPassword}
                    onChange={(e) => handleInputChange('userPassword', e.target.value)}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
                    placeholder="Enter password"
                  />
                </div>



                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-base sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={editingAssociation ? () => handleUpdateAssociation(editingAssociation) : handleAddAssociation}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-base sm:text-sm"
                  >
                    {editingAssociation ? 'Update' : 'Add'} Association
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}